import { memberDisplayName, type OrgMember, type WorkspaceGrant } from '@noconflict/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetGrant } from '../../../entities/member';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { toast } from '../../../shared/ui/sonner';
import { GrantRoleFields, type GrantTarget } from './fields';

/**
 * One row per target the caller may grant on: "All workspaces" (`workspaceId` null, needs `members.manage`)
 * and/or single workspaces (`grants.manage` there). Each select saves at once; "No access" revokes.
 */
export function GrantsEditor({
  org,
  member,
  grants,
  targets,
  onError,
}: {
  org: { id: string };
  member: OrgMember;
  /** The member's current grants, as far as the caller can see them. */
  grants: readonly WorkspaceGrant[];
  targets: readonly GrantTarget[];
  onError?: (message: string | null) => void;
}) {
  const { t } = useTranslation('member');
  const setGrant = useSetGrant(org.id);
  const [error, setError] = useState<string | null>(null);
  const name = memberDisplayName(member);

  function report(message: string | null) {
    setError(message);
    onError?.(message);
  }

  return (
    <div className="grid gap-3">
      <GrantRoleFields
        targets={targets}
        labelSuffix={name}
        disabled={setGrant.isPending}
        roleFor={(targetId) =>
          grants.find((g) => g.user_id === member.user_id && g.workspace_id === targetId)?.role ??
          null
        }
        onChange={async (targetId, role) => {
          const grant = grants.find(
            (g) => g.user_id === member.user_id && g.workspace_id === targetId,
          );
          report(null);
          try {
            await setGrant.mutateAsync({
              workspaceId: targetId,
              userId: member.user_id,
              role,
              grantId: grant?.id ?? null,
            });
            toast.success(t('grants.saved'));
          } catch (e) {
            report(identityErrorMessage(e));
          }
        }}
      />
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
